import React, { useState, useEffect } from 'react';
import { reportService, GeneratedReport, GenerateReportRequest } from '../services/reportService';
import { Table, TableColumn } from '../components/Table';
import './ReportsPage.css';

export const ReportsPage: React.FC = () => {
  const [reports, setReports] = useState<GeneratedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [viewingReport, setViewingReport] = useState<GeneratedReport | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const itemsPerPage = 50;

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadReports reads from state
  }, [currentPage]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string | number> = {
        limit: itemsPerPage,
      };

      if (currentPage > 1 && lastEvaluatedKey) {
        params.lastEvaluatedKey = lastEvaluatedKey;
      }

      const response = await reportService.listReports(params);
      setReports(response.reports);
      setTotalCount(response.count);
      setLastEvaluatedKey(response.lastEvaluatedKey);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (request: GenerateReportRequest) => {
    try {
      const report = await reportService.generateReport(request);
      await loadReports();
      setShowGenerateModal(false);
      alert(`Report "${report.title}" generated successfully!`);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to generate report');
    }
  };

  const handleExport = (report: GeneratedReport, format: 'json' | 'csv') => {
    try {
      let content: string;
      let filename: string;
      let mimeType: string;

      if (format === 'csv') {
        content = reportService.exportToCSV(report);
        filename = `${report.title.replace(/\s+/g, '_')}_${report.reportId}.csv`;
        mimeType = 'text/csv';
      } else {
        content = reportService.exportToJSON(report);
        filename = `${report.title.replace(/\s+/g, '_')}_${report.reportId}.json`;
        mimeType = 'application/json';
      }

      reportService.downloadFile(content, filename, mimeType);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to export report');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns: TableColumn<GeneratedReport>[] = [
    { header: 'Title', accessor: 'title', hideOnMobile: false },
    {
      header: 'Type',
      accessor: 'reportType',
      render: (value) => (value as string).replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      hideOnMobile: true,
    },
    { header: 'Format', accessor: 'format', hideOnMobile: true },
    {
      header: 'Status',
      accessor: 'status',
      render: (value, row) => (
        <span className={`status-badge status-${row.status}`}>{row.status}</span>
      ),
      hideOnMobile: true,
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: (value) => formatDate(value as string),
      hideOnMobile: true,
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <div className="table-actions">
          <button onClick={() => setViewingReport(row)} className="action-button view">
            View
          </button>
          <button onClick={() => handleExport(row, 'json')} className="action-button export">
            JSON
          </button>
          <button onClick={() => handleExport(row, 'csv')} className="action-button export">
            CSV
          </button>
        </div>
      ),
      dataLabel: '',
    },
  ];

  return (
    <div className="reports-page">
      <div className="reports-header">
        <h1>Reports</h1>
        <button className="generate-button" onClick={() => setShowGenerateModal(true)}>
          Generate Report
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading reports...</div>
      ) : (
        <>
          <div className="reports-stats">
            <div className="stat-card">
              <div className="stat-value">{totalCount}</div>
              <div className="stat-label">Total Reports</div>
            </div>
          </div>

          <Table
            columns={columns}
            data={reports}
            getRowId={(row) => row.reportId}
            emptyMessage="No reports found"
            containerClassName="reports-table-container"
            className="reports-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || reports.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {showGenerateModal && (
        <GenerateReportModal
          onClose={() => setShowGenerateModal(false)}
          onGenerate={handleGenerate}
        />
      )}

      {viewingReport && (
        <ViewReportModal
          report={viewingReport}
          onClose={() => setViewingReport(null)}
          onExport={handleExport}
        />
      )}
    </div>
  );
};

const GenerateReportModal: React.FC<{
  onClose: () => void;
  onGenerate: (request: GenerateReportRequest) => void;
}> = ({ onClose, onGenerate }) => {
  const [formData, setFormData] = useState<GenerateReportRequest>({
    reportType: 'platform_summary',
    title: '',
    description: '',
    format: 'json',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onGenerate(formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Generate Report</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Report Type *</label>
            <select
              value={formData.reportType}
              onChange={(e) => setFormData({ ...formData, reportType: e.target.value as GenerateReportRequest['reportType'] })}
              required
            >
              <option value="platform_summary">Platform Summary</option>
              <option value="user_analytics">User Analytics</option>
              <option value="booking_analytics">Booking Analytics</option>
              <option value="revenue_analytics">Revenue Analytics</option>
              <option value="custom">Custom (All Data)</option>
            </select>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="e.g., Monthly Platform Summary"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Date Range (Optional)</label>
            <div className="date-range">
              <input
                type="date"
                value={formData.dateRange?.startDate || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dateRange: { ...formData.dateRange, startDate: e.target.value },
                  })
                }
              />
              <span>to</span>
              <input
                type="date"
                value={formData.dateRange?.endDate || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dateRange: { ...formData.dateRange, endDate: e.target.value },
                  })
                }
              />
            </div>
          </div>
          <div className="form-group">
            <label>Format *</label>
            <select
              value={formData.format}
              onChange={(e) => setFormData({ ...formData, format: e.target.value as 'json' | 'csv' })}
              required
            >
              <option value="json">JSON</option>
              <option value="csv">CSV</option>
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" disabled={loading}>
              {loading ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ViewReportModal: React.FC<{
  report: GeneratedReport;
  onClose: () => void;
  onExport: (report: GeneratedReport, format: 'json' | 'csv') => void;
}> = ({ report, onClose, onExport }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <h2>{report.title}</h2>
        <div className="report-info">
          <div><strong>Type:</strong> {report.reportType.replace(/_/g, ' ')}</div>
          <div><strong>Format:</strong> {report.format.toUpperCase()}</div>
          <div><strong>Status:</strong> {report.status}</div>
          <div><strong>Created:</strong> {new Date(report.createdAt).toLocaleString()}</div>
          {report.description && <div><strong>Description:</strong> {report.description}</div>}
        </div>
        <div className="report-actions">
          <button onClick={() => onExport(report, 'json')} className="export-button">
            Export JSON
          </button>
          <button onClick={() => onExport(report, 'csv')} className="export-button">
            Export CSV
          </button>
        </div>
        <div className="report-data">
          <pre>{JSON.stringify(report.data, null, 2)}</pre>
        </div>
        <div className="modal-actions">
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};
